import { RbacChip, typenameChipMap } from "./RbacChip";

interface GetTargetCountMapParams {
  /** The target key is the first part of the target string, e.g. "account-group:123" - "account-group" */
  targets: string[];
}

/** Sums the count for each role assignment target key returning a map with key:count */
function getTargetCountMap({ targets }: GetTargetCountMapParams) {
  const safeTargets = Array.isArray(targets) ? targets : [];

  return safeTargets.reduce<Record<string, number>>((acc, v) => {
    const [typeName] = v.split(":");
    if (!typeName) return acc;
    acc[typeName] = (acc[typeName] || 0) + 1;
    return acc;
  }, {});
}

interface TargetDetail {
  type: string;
  name?: string;
}

interface RbacTargetChipGroupProps {
  targets: string[];
  targetDetails?: TargetDetail[];
  showLabels?: boolean;
}

/** Displays the rbac target chips in a table cell */
export function RbacTargetChipGroup({
  targets,
  targetDetails,
  showLabels = false,
}: RbacTargetChipGroupProps) {
  // Show detailed labeled chips if available and requested
  if (showLabels && targetDetails?.length) {
    return (
      <div className="flex w-full flex-wrap gap-xs">
        {targetDetails.map((target, i) => {
          const rbacType = typenameChipMap[target.type];
          if (!rbacType) return null;

          return (
            <RbacChip
              key={`${target.type}:${target.name || ""}-${i}`}
              label={target.name}
              rbacType={rbacType}
            />
          );
        })}
      </div>
    );
  }

  // Otherwise use counts
  const typeNameMap = getTargetCountMap({ targets });
  const typenames = Object.keys(typeNameMap);

  if (!typenames.length) return <div />;

  return (
    <div className="flex gap-xs">
      {typenames.map((typename) => {
        const rbacType = typenameChipMap[typename];
        const showCount = !["System", "RoleScope"].includes(typename);

        return (
          <div className="flex items-center gap-xs" key={typename}>
            {showCount ? (
              <span className="text-label-small text-legacy-navy-L30">
                {typeNameMap[typename]}
              </span>
            ) : null}
            <RbacChip rbacType={rbacType} variant="column" />
          </div>
        );
      })}
    </div>
  );
}
